import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2, ArrowRight, ArrowLeft, Package, MapPin, Building2, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RFQ = () => {
  const { t, currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    product_name: searchParams.get('product') || '',
    category: searchParams.get('category') || '',
    quantity: '',
    unit: 'mt',
    destination_port: '',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const canProceedStep1 = formData.product_name && formData.category && formData.quantity && formData.destination_port;
  const canProceedStep2 = formData.company_name && formData.contact_name && formData.email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/rfq`, {
        ...formData,
        language: currentLanguage
      });
      setSubmitted(true);
      toast.success(t('rfq.form.success'));
    } catch (error) {
      console.error('RFQ submission failed:', error);
      toast.error(t('rfq.form.error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f9f9f7] pt-24 flex items-center justify-center" data-testid="rfq-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center px-6"
        >
          <div className="w-24 h-24 bg-[#0a1628] flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-12 h-12 text-secondary-500" />
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-4">
            Request Submitted
          </h2>
          <p className="text-muted-foreground mb-10 leading-relaxed">
            Thank you for your interest. Our team will review your request and respond within 24-48 business hours with a competitive quote.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setStep(1);
              setFormData({
                product_name: '',
                category: '',
                quantity: '',
                unit: 'mt',
                destination_port: '',
                company_name: '',
                contact_name: '',
                email: '',
                phone: '',
                message: '',
              });
            }}
            className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest"
          >
            Submit Another Request
          </Button>
        </motion.div>
      </div>
    );
  }

  const steps = [
    { num: 1, label: 'Product Details', icon: Package },
    { num: 2, label: 'Contact Info', icon: User },
    { num: 3, label: 'Review', icon: Send },
  ];

  return (
    <div className="min-h-screen bg-[#f9f9f7]" data-testid="rfq-page">
      {/* Header */}
      <section className="bg-[#0a1628] relative overflow-hidden pt-20 sm:pt-24">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16 md:py-20 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-secondary-400 mb-4">
              {t('rfq.get_pricing')}
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white tracking-tight mb-4 sm:mb-6">
              {t('rfq.title')}
            </h1>
            <p className="text-base sm:text-lg text-white/60 max-w-xl">
              {t('rfq.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-12 py-8 sm:py-12 md:py-16">
        {/* Step Indicator */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center justify-between">
            {steps.map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 flex items-center justify-center transition-all duration-300 ${
                    step >= s.num ? 'bg-[#0a1628] text-white' : 'bg-white border border-border text-muted-foreground'
                  }`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className={`hidden md:block text-sm font-medium transition-colors ${
                    step >= s.num ? 'text-primary-900' : 'text-muted-foreground'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div className={`flex-1 h-px mx-4 transition-colors ${
                    step > s.num ? 'bg-[#0a1628]' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Product Details */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white border border-border p-8 md:p-12"
              >
                <div className="flex items-center gap-3 mb-8">
                  <Package className="w-6 h-6 text-secondary-500" />
                  <h2 className="text-2xl font-heading font-bold text-primary-900">Product Details</h2>
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        {t('rfq.form.product')} *
                      </label>
                      <Input
                        name="product_name"
                        value={formData.product_name}
                        onChange={handleChange}
                        placeholder="e.g., Basmati Rice"
                        required
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-product"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        {t('rfq.form.category')} *
                      </label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => handleSelectChange('category', value)}
                      >
                        <SelectTrigger className="h-14 rounded-none border-border" data-testid="rfq-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        {t('rfq.form.quantity')} *
                      </label>
                      <div className="flex">
                        <Input
                          name="quantity"
                          type="number"
                          value={formData.quantity}
                          onChange={handleChange}
                          placeholder="100"
                          required
                          className="h-14 rounded-none border-border flex-1"
                          data-testid="rfq-quantity"
                        />
                        <Select
                          value={formData.unit}
                          onValueChange={(value) => handleSelectChange('unit', value)}
                        >
                          <SelectTrigger className="h-14 rounded-none border-border border-l-0 w-32" data-testid="rfq-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mt">{t('rfq.units.mt')}</SelectItem>
                            <SelectItem value="kg">{t('rfq.units.kg')}</SelectItem>
                            <SelectItem value="units">{t('rfq.units.units')}</SelectItem>
                            <SelectItem value="containers">{t('rfq.units.containers')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {t('rfq.form.destination')} *
                      </label>
                      <Input
                        name="destination_port"
                        value={formData.destination_port}
                        onChange={handleChange}
                        placeholder="e.g., Port of Rotterdam"
                        required
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-destination"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-10 pt-8 border-t border-border">
                  <Button
                    type="button"
                    disabled={!canProceedStep1}
                    onClick={() => setStep(2)}
                    className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest group disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Info */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white border border-border p-8 md:p-12"
              >
                <div className="flex items-center gap-3 mb-8">
                  <User className="w-6 h-6 text-secondary-500" />
                  <h2 className="text-2xl font-heading font-bold text-primary-900">Contact Information</h2>
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        <Building2 className="w-3 h-3 inline mr-1" />
                        {t('rfq.form.company')} *
                      </label>
                      <Input
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        placeholder="Your company name"
                        required
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-company"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        {t('rfq.form.contact')} *
                      </label>
                      <Input
                        name="contact_name"
                        value={formData.contact_name}
                        onChange={handleChange}
                        placeholder="Your name"
                        required
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-contact"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {t('rfq.form.email')} *
                      </label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {t('rfq.form.phone')}
                      </label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 890"
                        className="h-14 rounded-none border-border"
                        data-testid="rfq-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      {t('rfq.form.message')}
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Any specific requirements..."
                      rows={4}
                      className="rounded-none border-border resize-none"
                      data-testid="rfq-message"
                    />
                  </div>
                </div>

                <div className="flex justify-between mt-10 pt-8 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="rounded-none border-2 border-primary-900 text-primary-900 hover:bg-[#0a1628] hover:text-white px-8 py-6 text-xs font-bold uppercase tracking-widest"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="button"
                    disabled={!canProceedStep2}
                    onClick={() => setStep(3)}
                    className="bg-[#0a1628] hover:bg-primary-800 text-white rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest group disabled:opacity-50"
                  >
                    Review
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white border border-border p-8 md:p-12"
              >
                <div className="flex items-center gap-3 mb-8">
                  <Send className="w-6 h-6 text-secondary-500" />
                  <h2 className="text-2xl font-heading font-bold text-primary-900">Review Your Request</h2>
                </div>

                <div className="space-y-8">
                  {/* Product Summary */}
                  <div className="p-6 bg-muted/50">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Product Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Product</p>
                        <p className="font-medium text-primary-900">{formData.product_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Category</p>
                        <p className="font-medium text-primary-900">{categories.find(c => c.id === formData.category)?.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                        <p className="font-mono font-medium text-primary-900">{formData.quantity} {formData.unit.toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Destination</p>
                        <p className="font-medium text-primary-900">{formData.destination_port}</p>
                      </div>
                    </div>
                  </div>

                  {/* Contact Summary */}
                  <div className="p-6 bg-muted/50">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Contact Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Company</p>
                        <p className="font-medium text-primary-900">{formData.company_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Contact</p>
                        <p className="font-medium text-primary-900">{formData.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Email</p>
                        <p className="font-medium text-primary-900">{formData.email}</p>
                      </div>
                      {formData.phone && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Phone</p>
                          <p className="font-medium text-primary-900">{formData.phone}</p>
                        </div>
                      )}
                    </div>
                    {formData.message && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground mb-1">Message</p>
                        <p className="text-primary-900">{formData.message}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-between mt-10 pt-8 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="rounded-none border-2 border-primary-900 text-primary-900 hover:bg-[#0a1628] hover:text-white px-8 py-6 text-xs font-bold uppercase tracking-widest"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-secondary-500 hover:bg-secondary-400 text-primary-900 rounded-none px-10 py-6 text-xs font-bold uppercase tracking-widest"
                    data-testid="rfq-submit"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-primary-900/30 border-t-primary-900 rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('rfq.form.submit')}
                      </span>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
};

export default RFQ;
